import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContactForm from '@/pages/about/components/ContactForm';

const contactItems = [
  { icon: 'ri-mail-line', label: '联系邮箱', value: 'contact@eimi-fukada-demo.com' },
  { icon: 'ri-wechat-2-line', label: '合作微信', value: 'eimi_media_demo' },
  { icon: 'ri-phone-line', label: '商务电话', value: '+86 400-000-0000' },
];

const socialLinks = [
  { icon: 'ri-weibo-fill', label: '微博', handle: '@深田咏美资讯站' },
  { icon: 'ri-youtube-fill', label: '视频频道', handle: 'Eimi Fukada Info' },
  { icon: 'ri-instagram-line', label: '图文社区', handle: '@eimi.info' },
  { icon: 'ri-bilibili-fill', label: 'B站', handle: '深田咏美资讯' },
];

const scope = [
  { icon: 'ri-newspaper-line', title: '新闻动态', desc: '第一时间跟进深田咏美的最新动态、活动报道与媒体采访。' },
  { icon: 'ri-film-line', title: '作品资料', desc: '整理她参演的电影、电视剧与纪录片，提供完整作品介绍。' },
  { icon: 'ri-user-star-line', title: '明星资料', desc: '汇集个人简介、从艺经历与代表作品，帮助影迷全面了解。' },
];

export default function AboutPage() {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      setCopied(null);
    }
  };

  return (
    <>
      <Seo
        title="关于我们-深田咏美资讯平台｜深田咏美资讯"
        description="了解深田咏美资讯平台的内容定位、网站使命与联系方式。我们致力于为影迷与资讯关注者提供专业、及时的明星资讯服务。"
        keywords="深田咏美资讯,关于我们,深田咏美网站"
      />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="max-w-6xl mx-auto px-4 py-12 md:px-8 md:py-16">
          <div className="mb-5 [&_*]:text-background-300">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>
          <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">关于我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
            一个专注于深田咏美的明星资讯平台，致力于为影迷与资讯关注者提供专业、权威、更新及时的内容服务。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto grid grid-cols-1 gap-12 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-2">
          <div>
            <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              我们的使命
            </h2>
            <div className="mt-6 space-y-5 text-base leading-loose text-foreground-700">
              <p>
                本站以「深田咏美」为核心，打造一个专业、权威且更新及时的明星资讯平台，面向深田咏美粉丝、影视观众、娱乐业界人士及明星资讯爱好者。
              </p>
              <p>
                我们相信，优质的资讯应当清晰、准确、有温度。因此，从作品介绍到活动报道，从人物资料到幕后故事，我们都力求用自然的文字与统一的结构，帮助每一位读者快速获取想要的信息。
              </p>
              <p>
                本站内容为演示数据，仅用于展示平台的信息组织方式与阅读体验，不构成任何真实信息的发布与背书。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {scope.map((s) => (
                <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${s.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <p className="mt-1.5 text-xs text-foreground-500">点击任意条目即可复制（演示数据）</p>
              <ul className="mt-5 space-y-3">
                {contactItems.map((c) => (
                  <li key={c.label}>
                    <button
                      type="button"
                      onClick={() => handleCopy(c.value)}
                      className="group flex w-full items-center gap-4 rounded-md border border-background-200 bg-background-50 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                        <i className={`${c.icon} text-lg`}></i>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-secondary-500">{c.label}</span>
                        <span className="block truncate text-sm font-medium text-foreground-900">{c.value}</span>
                      </span>
                      <i
                        className={
                          copied === c.value
                            ? 'ri-check-line text-primary-600'
                            : 'ri-file-copy-line text-secondary-400 group-hover:text-primary-600'
                        }
                      ></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">社交媒体</h2>
              <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {socialLinks.map((s) => (
                  <li key={s.label}>
                    <a
                      href="#social"
                      rel="nofollow"
                      className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                    >
                      <i className={`${s.icon} text-lg text-primary-600`}></i>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900">{s.label}</span>
                        <span className="block truncate text-xs text-secondary-500">{s.handle}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 gap-10 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              版权与声明
            </h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-foreground-600">
              <p>
                本站为粉丝资讯演示站点，站内所有人物简介、作品资料、新闻内容及相关图片均为演示数据，不代表任何真实的官方信息。
              </p>
              <p>
                如需了解真实信息，请以官方渠道发布的内容为准。本站文字与结构仅供展示平台的资讯组织方式，请勿用于任何未经授权的商业用途。
              </p>
              <p className="text-foreground-500">© 2026 深田咏美明星资讯网 · 保留所有权利</p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200/60 cursor-pointer"
                >
                  <i className="ri-newspaper-line"></i>
                  新闻中心
                </Link>
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200/60 cursor-pointer"
                >
                  <i className="ri-film-line"></i>
                  影视作品
                </Link>
              </div>
            </div>
          </div>

          <ContactForm />
        </div>
      </section>
    </>
  );
}